import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSector } from '../contexts/SectorContext';
import { ConfirmModal } from '../components/SharedModal';
import Switch from '../components/Switch/Switch';
import {
  AttendanceOptions,
  HoursDay,
  SlaRule,
  SlaRuleWrite,
  SlaStatusRow,
  createSlaRule,
  deleteSlaRule,
  getAttendanceOptions,
  getSla,
  getSlaStatus,
  saveSlaSettings,
  updateSlaRule,
} from '../services/AttendanceService';
import './AttendancePage.css';

const DAY_NAMES = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

type DayDraft = { open: boolean; openAt: string; closeAt: string };

const defaultDays = (): DayDraft[] =>
  DAY_NAMES.map((_, day) => ({ open: day >= 1 && day <= 5, openAt: '09:00', closeAt: '18:00' }));

function daysFrom(hours: HoursDay[] | null): DayDraft[] {
  if (!hours) return defaultDays();
  return DAY_NAMES.map((_, day) => {
    const entry = hours.find((h) => h.day === day);
    return entry
      ? { open: true, openAt: entry.open, closeAt: entry.close }
      : { open: false, openAt: '09:00', closeAt: '18:00' };
  });
}

export function formatDeadline(minutes: number): string {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? '1 dia' : `${days} dias`;
  }
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

type RuleDraft = {
  name: string;
  scopeKind: string;
  scopeValue: string;
  amount: string;
  unit: 'minutes' | 'hours';
  aiMayReply: boolean;
  escalateTo: string;
  enabled: boolean;
};

const emptyRule = (): RuleDraft => ({
  name: '',
  scopeKind: 'all',
  scopeValue: '',
  amount: '30',
  unit: 'minutes',
  aiMayReply: true,
  escalateTo: '',
  enabled: true,
});

const SCOPE_KINDS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todas as conversas' },
  { value: 'channel', label: 'Canal' },
  { value: 'origin', label: 'Origem' },
  { value: 'form', label: 'Formulário do Google Ads' },
  { value: 'campaign', label: 'Campanha' },
];

function scopeChoices(kind: string, options: AttendanceOptions | null) {
  if (!options) return [];
  switch (kind) {
    case 'channel': return options.channels;
    case 'origin': return options.origins;
    case 'form': return options.forms;
    case 'campaign': return options.campaigns;
    default: return [];
  }
}

function scopeLabel(rule: SlaRule, options: AttendanceOptions | null): string {
  if (rule.scopeKind === 'all') return 'Todas as conversas';
  const kind = SCOPE_KINDS.find((k) => k.value === rule.scopeKind)?.label ?? rule.scopeKind;
  const found = scopeChoices(rule.scopeKind, options).find((o) => o.value === rule.scopeValue);
  return `${kind}: ${found?.label ?? rule.scopeValue ?? ''}`;
}

function formatWhen(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Fora do prazo e pedindo resposta (a situação vem do servidor; "Não pede resposta" não é atraso). */
function isLate(row: SlaStatusRow): boolean {
  return row.status === 'Breached' && row.reason !== 'no_reply_needed';
}

const AttendancePage: React.FC = () => {
  const { selectedSectorId } = useSector();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<SlaRule[]>([]);
  const [options, setOptions] = useState<AttendanceOptions | null>(null);
  const [status, setStatus] = useState<SlaStatusRow[] | null>(null);
  const [statusError, setStatusError] = useState('');
  const [loadError, setLoadError] = useState('');

  const [mode, setMode] = useState<'24h' | 'custom'>('24h');
  const [days, setDays] = useState<DayDraft[]>(defaultDays);
  const [hoursError, setHoursError] = useState('');
  const [hoursNotice, setHoursNotice] = useState('');
  const [savingHours, setSavingHours] = useState(false);

  const [draft, setDraft] = useState<RuleDraft>(emptyRule);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [ruleError, setRuleError] = useState('');
  const [savingRule, setSavingRule] = useState(false);
  const [toDelete, setToDelete] = useState<SlaRule | null>(null);

  const userName = useMemo(() => {
    const map = new Map<number, string>();
    (options?.users ?? []).forEach((u) => map.set(u.id, u.name));
    return map;
  }, [options]);

  const loadStatus = useCallback(async () => {
    if (!selectedSectorId) return;
    setStatusError('');
    try {
      setStatus(await getSlaStatus(selectedSectorId));
    } catch (err) {
      setStatus([]);
      setStatusError((err as Error).message);
    }
  }, [selectedSectorId]);

  const loadAll = useCallback(async () => {
    if (!selectedSectorId) return;
    setLoading(true);
    setLoadError('');
    try {
      const [sla, opts] = await Promise.all([getSla(selectedSectorId), getAttendanceOptions(selectedSectorId)]);
      setRules(sla.rules);
      setOptions(opts);
      const hours = sla.settings.businessHours;
      setMode(hours ? 'custom' : '24h');
      setDays(daysFrom(hours));
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setLoading(false);
    }
    void loadStatus();
  }, [selectedSectorId, loadStatus]);

  useEffect(() => {
    setDraft(emptyRule());
    setEditingId(null);
    setRuleError('');
    setHoursError('');
    setHoursNotice('');
    setStatus(null);
    void loadAll();
  }, [loadAll]);

  const onSaveHours = async () => {
    if (!selectedSectorId) return;
    setHoursError('');
    setHoursNotice('');
    if (mode === 'custom' && !days.some((d) => d.open)) {
      setHoursError('Escolha ao menos um dia aberto.');
      return;
    }
    setSavingHours(true);
    try {
      const hours: HoursDay[] | null = mode === '24h'
        ? null
        : days
            .map((d, day) => ({ d, day }))
            .filter(({ d }) => d.open)
            .map(({ d, day }) => ({ day, open: d.openAt, close: d.closeAt }));
      await saveSlaSettings(selectedSectorId, hours);
      setHoursNotice('Horário salvo.');
      void loadStatus();
    } catch (err) {
      setHoursError((err as Error).message);
    } finally {
      setSavingHours(false);
    }
  };

  const setDay = (index: number, patch: Partial<DayDraft>) =>
    setDays((current) => current.map((d, i) => (i === index ? { ...d, ...patch } : d)));

  const writeFrom = (source: RuleDraft): SlaRuleWrite => {
    const amount = Number(source.amount);
    return {
      sectorId: selectedSectorId!,
      name: source.name.trim(),
      scopeKind: source.scopeKind,
      scopeValue: source.scopeKind === 'all' ? null : source.scopeValue || null,
      deadlineMinutes: Math.round(source.unit === 'hours' ? amount * 60 : amount),
      aiMayReply: source.aiMayReply,
      enabled: source.enabled,
      escalateToUserId: source.escalateTo ? Number(source.escalateTo) : null,
    };
  };

  const resetForm = () => {
    setDraft(emptyRule());
    setEditingId(null);
    setRuleError('');
  };

  const onSaveRule = async () => {
    if (!selectedSectorId) return;
    setRuleError('');
    setSavingRule(true);
    try {
      const body = writeFrom(draft);
      if (editingId != null) await updateSlaRule(editingId, body);
      else await createSlaRule(body);
      resetForm();
      const sla = await getSla(selectedSectorId);
      setRules(sla.rules);
      void loadStatus();
    } catch (err) {
      setRuleError((err as Error).message);
    } finally {
      setSavingRule(false);
    }
  };

  const startEdit = (rule: SlaRule) => {
    const hours = rule.deadlineMinutes >= 60 && rule.deadlineMinutes % 60 === 0;
    setEditingId(rule.id);
    setRuleError('');
    setDraft({
      name: rule.name,
      scopeKind: rule.scopeKind,
      scopeValue: rule.scopeValue ?? '',
      amount: String(hours ? rule.deadlineMinutes / 60 : rule.deadlineMinutes),
      unit: hours ? 'hours' : 'minutes',
      aiMayReply: rule.aiMayReply,
      escalateTo: rule.escalateToUserId ? String(rule.escalateToUserId) : '',
      enabled: rule.enabled,
    });
  };

  const toggleRule = async (rule: SlaRule, enabled: boolean) => {
    if (!selectedSectorId) return;
    setRuleError('');
    try {
      await updateSlaRule(rule.id, {
        sectorId: selectedSectorId,
        name: rule.name,
        scopeKind: rule.scopeKind,
        scopeValue: rule.scopeValue,
        deadlineMinutes: rule.deadlineMinutes,
        aiMayReply: rule.aiMayReply,
        enabled,
        escalateToUserId: rule.escalateToUserId,
      });
      setRules((await getSla(selectedSectorId)).rules);
      void loadStatus();
    } catch (err) {
      setRuleError((err as Error).message);
    }
  };

  const confirmDelete = async () => {
    if (!selectedSectorId || !toDelete) return false;
    try {
      await deleteSlaRule(toDelete.id, selectedSectorId);
      if (editingId === toDelete.id) resetForm();
      setRules((await getSla(selectedSectorId)).rules);
      void loadStatus();
      return true;
    } catch (err) {
      setRuleError((err as Error).message);
      return true;
    }
  };

  const choices = scopeChoices(draft.scopeKind, options);

  return (
    <div className="attendance">
      <header className="attendance-header">
        <h1>Prazo e roteamento</h1>
        <p>
          O prazo só conta dentro do horário de atendimento. Mensagens que não pedem resposta, como “ok” ou “👍”, não
          geram alerta. Quando o prazo vence e a IA pode responder, ela responde sozinha; a equipe só é avisada quando
          a IA não pode ou não conseguiu.
        </p>
        <div className="attendance-tabs" role="tablist">
          <button type="button" role="tab" aria-selected className="attendance-tab is-active">
            Prazo de atendimento
          </button>
        </div>
      </header>

      <div className="attendance-body">
        {loadError && <p className="attendance-error" role="alert">{loadError}</p>}
        {loading && !loadError && <p className="attendance-empty" aria-busy="true">Carregando…</p>}

        <section className="attendance-panel" aria-labelledby="attendance-hours-title">
          <h2 id="attendance-hours-title">Horário de atendimento</h2>
          <p className="attendance-panel-hint">Fora do horário o prazo pausa e volta a contar quando a equipe abrir.</p>
          <div className="attendance-radios">
            <label>
              <input type="radio" name="attendance-mode" checked={mode === '24h'} onChange={() => setMode('24h')} />
              24 horas
            </label>
            <label>
              <input type="radio" name="attendance-mode" checked={mode === 'custom'} onChange={() => setMode('custom')} />
              Só no horário abaixo
            </label>
          </div>
          {mode === 'custom' && (
            <div className="attendance-hours">
              {DAY_NAMES.map((name, index) => (
                <div key={name} className={`attendance-hours-row${days[index].open ? '' : ' is-closed'}`}>
                  <Switch checked={days[index].open} onChange={(next) => setDay(index, { open: next })} label={name} />
                  <input
                    type="time"
                    aria-label={`Abertura ${name}`}
                    value={days[index].openAt}
                    disabled={!days[index].open}
                    onChange={(e) => setDay(index, { openAt: e.target.value })}
                  />
                  <input
                    type="time"
                    aria-label={`Fechamento ${name}`}
                    value={days[index].closeAt}
                    disabled={!days[index].open}
                    onChange={(e) => setDay(index, { closeAt: e.target.value })}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="attendance-actions">
            <button type="button" className="attendance-btn" onClick={onSaveHours} disabled={savingHours}>
              Salvar horário
            </button>
          </div>
          {hoursError && <p className="attendance-error" role="alert">{hoursError}</p>}
          {hoursNotice && <p className="attendance-notice">{hoursNotice}</p>}
        </section>

        <section className="attendance-panel" aria-labelledby="attendance-rules-title">
          <h2 id="attendance-rules-title">Regras de prazo</h2>
          <p className="attendance-panel-hint">
            Vale a regra ativa mais específica: campanha, depois formulário, origem, canal e, por último, todas as
            conversas. Sem nenhuma regra, nada muda.
          </p>
          {rules.length === 0 ? (
            <p className="attendance-empty">Nenhuma regra ainda. Sem regra, a MENDS não cobra prazo.</p>
          ) : (
            <ul className="attendance-rules">
              {rules.map((rule) => (
                <li key={rule.id} className={`attendance-rule${rule.enabled ? '' : ' is-off'}`}>
                  <div>
                    <span className="attendance-rule-name">{rule.name}</span>
                    <p className="attendance-rule-meta">
                      {`Vale para: ${scopeLabel(rule, options)} · Prazo: ${formatDeadline(rule.deadlineMinutes)} · ${
                        rule.aiMayReply ? 'IA responde sozinha' : 'Só avisa a equipe'
                      } · Responsável: ${
                        rule.escalateToUserId ? userName.get(rule.escalateToUserId) ?? `usuário ${rule.escalateToUserId}` : 'responsável pela conversa'
                      }`}
                    </p>
                  </div>
                  <div className="attendance-rule-side">
                    <Switch checked={rule.enabled} onChange={(next) => void toggleRule(rule, next)} label="Ativa" />
                    <button type="button" className="attendance-btn is-secondary is-small" onClick={() => startEdit(rule)}>
                      Editar
                    </button>
                    <button type="button" className="attendance-btn is-danger is-small" onClick={() => setToDelete(rule)}>
                      Excluir
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <h2>{editingId != null ? 'Editar regra' : 'Nova regra'}</h2>
          <div className="attendance-form">
            <div className="attendance-field">
              <label htmlFor="attendance-rule-name">Nome</label>
              <input
                id="attendance-rule-name"
                type="text"
                value={draft.name}
                maxLength={80}
                placeholder="Ex.: Anúncios · 15 min"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div className="attendance-field">
              <label htmlFor="attendance-rule-scope">Vale para</label>
              <select
                id="attendance-rule-scope"
                value={draft.scopeKind}
                onChange={(e) => setDraft({ ...draft, scopeKind: e.target.value, scopeValue: '' })}
              >
                {SCOPE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            {draft.scopeKind !== 'all' && (
              <div className="attendance-field">
                <label htmlFor="attendance-rule-target">Alvo</label>
                <select
                  id="attendance-rule-target"
                  value={draft.scopeValue}
                  onChange={(e) => setDraft({ ...draft, scopeValue: e.target.value })}
                >
                  <option value="">Escolha…</option>
                  {choices.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}
            <div className="attendance-field">
              <label htmlFor="attendance-rule-amount">Prazo</label>
              <div className="attendance-deadline">
                <input
                  id="attendance-rule-amount"
                  type="number"
                  min={1}
                  value={draft.amount}
                  onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                />
                <select
                  aria-label="Unidade do prazo"
                  value={draft.unit}
                  onChange={(e) => setDraft({ ...draft, unit: e.target.value as 'minutes' | 'hours' })}
                >
                  <option value="minutes">minutos</option>
                  <option value="hours">horas</option>
                </select>
              </div>
            </div>
            <div className="attendance-field">
              <label htmlFor="attendance-rule-recipient">Quem recebe o aviso</label>
              <select
                id="attendance-rule-recipient"
                value={draft.escalateTo}
                onChange={(e) => setDraft({ ...draft, escalateTo: e.target.value })}
              >
                <option value="">Responsável pela conversa</option>
                {(options?.users ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="attendance-form-switches">
              <Switch
                checked={draft.aiMayReply}
                onChange={(next) => setDraft({ ...draft, aiMayReply: next })}
                label="A IA pode responder sozinha quando o prazo vencer"
              />
              <Switch checked={draft.enabled} onChange={(next) => setDraft({ ...draft, enabled: next })} label="Regra ativa" />
            </div>
          </div>
          <div className="attendance-actions">
            <button type="button" className="attendance-btn" onClick={onSaveRule} disabled={savingRule}>
              Salvar regra
            </button>
            {editingId != null && (
              <button type="button" className="attendance-btn is-secondary" onClick={resetForm}>
                Cancelar edição
              </button>
            )}
          </div>
          {ruleError && <p className="attendance-error" role="alert">{ruleError}</p>}
        </section>

        <section className="attendance-panel" aria-labelledby="attendance-status-title">
          <h2 id="attendance-status-title">Situação agora</h2>
          <p className="attendance-panel-hint">Conversas em que o cliente falou por último e ninguém respondeu ainda.</p>
          {statusError && <p className="attendance-error" role="alert">{statusError}</p>}
          {status && status.length === 0 && !statusError ? (
            <p className="attendance-empty">Nenhuma conversa esperando resposta nesta conexão.</p>
          ) : (
            status && status.length > 0 && (
              <div className="attendance-table-wrap">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Esperando desde</th>
                      <th>Regra</th>
                      <th>Prazo</th>
                      <th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.map((row) => (
                      <tr key={row.contactId}>
                        <td>{row.contactName}</td>
                        <td>{formatWhen(row.waitingSinceUtc)}</td>
                        <td>{row.ruleName ?? '—'}</td>
                        <td>{row.deadlineMinutes != null ? formatDeadline(row.deadlineMinutes) : '—'}</td>
                        <td><span className={`attendance-status${isLate(row) ? ' is-late' : ''}`}>{row.label}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
          <div className="attendance-actions">
            <button type="button" className="attendance-btn is-secondary" onClick={() => void loadStatus()}>
              Atualizar
            </button>
          </div>
        </section>
      </div>

      <ConfirmModal
        open={toDelete != null}
        onClose={() => setToDelete(null)}
        onConfirm={confirmDelete}
        title="Excluir regra?"
        message={toDelete ? `Excluir a regra “${toDelete.name}”? Os avisos abertos dela somem do Hoje.` : ''}
        confirmLabel="Excluir regra"
        cancelLabel="Cancelar"
        variant="danger"
      />
    </div>
  );
};

export default AttendancePage;
